/**
 * Launch service helpers for the MineTwin admin portal.
 *
 * Implements the helper surface required by [docs/ORIENTATION-ADMIN-MVP1.md §C]
 * (`detectPlatform`, `getLauncherDownloadUrl`, `addBaseUrlToProtocol`,
 * `startHelper`, `getStatusMessage`, `isHelperOnline`).
 *
 * Auto-update is launcher-side; the portal only needs to pass `base_url`
 * and `stdb_url` through the `turbotwin://start?token=...` protocol URL.
 *
 * NOTE: The launcher EXE itself is the existing TurboTwin signed binary
 * (decision D8b in BIBLE.md). The portal does NOT re-sign or rebrand the
 * installer for MVP-1.
 */

export type Platform = 'windows' | 'mac' | 'linux';

/** Maps `navigator.userAgent` / `navigator.platform` to launcher platform key. */
export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'linux';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('win')) return 'windows';
  if (ua.includes('mac') || ua.includes('darwin')) return 'mac';
  return 'linux';
}

/**
 * OS-specific download URL for the launcher installer.
 *
 * Defaults to the same Azure Blob Storage layout the TurboTwin webclient uses
 * (`https://assetsturbotwindev.blob.core.windows.net/downloads/<file>` since
 * the 2026-09-23 dev2 migration — a launcher from the old `assetsturbotwin`
 * host cannot update itself or reach the new cluster), so the Download button
 * works out-of-the-box. Override via:
 *  - `VITE_LAUNCHER_BLOB_BASE` to point at a different CDN host, or
 *  - `VITE_LAUNCHER_DOWNLOAD_WINDOWS` / `_MAC` / `_LINUX` to fully replace a
 *    single platform's URL.
 *
 * A cache-bust query param matches the webclient so the user always gets the
 * latest signed binary even if the CDN has aggressive caching.
 */
export function getLauncherDownloadUrl(platform: Platform = detectPlatform()): string | null {
  const env = import.meta.env;
  const override: Record<Platform, string | undefined> = {
    windows: env.VITE_LAUNCHER_DOWNLOAD_WINDOWS,
    mac: env.VITE_LAUNCHER_DOWNLOAD_MAC,
    linux: env.VITE_LAUNCHER_DOWNLOAD_LINUX,
  };
  if (override[platform]) return override[platform] as string;

  const base =
    env.VITE_LAUNCHER_BLOB_BASE ??
    'https://assetsturbotwindev.blob.core.windows.net/downloads';
  const filename: Record<Platform, string> = {
    windows: 'turbotwin-launcher.exe',
    mac: 'turbotwin-launcher-macos',
    linux: 'turbotwin-launcher-linux',
  };
  const cacheBust = `?t=${Math.floor(Date.now() / 1000)}`;
  return `${base}/${filename[platform]}${cacheBust}`;
}

/**
 * URL-safe encoding for params that go through a Windows protocol handler.
 *
 * Standard `%XX` encoding gets mangled by Windows when handing the URL off
 * to the registered handler EXE (colons, slashes, question marks come
 * through corrupted). The TurboTwin launcher expects a custom scheme using
 * placeholder character sequences that don't collide with valid URL chars.
 * Mirror of the webclient's `encodeUrlForProtocol` (LaunchService.ts:47).
 */
function encodeUrlForProtocol(url: string): string {
  return url
    .replace(/:/g, '_C_')
    .replace(/\//g, '_S_')
    .replace(/\?/g, '_Q_')
    .replace(/&/g, '_A_')
    .replace(/=/g, '_E_');
}

/**
 * Builds the `turbotwin://start?...` protocol URL the launcher EXE handles.
 *
 * The launcher does its own WorkOS authorization once started — the portal
 * does NOT pass an access token. It just passes context:
 *   - `base_url` — where to come back to when auth completes
 *   - `stdb_url` — which SpacetimeDB to connect to
 *   - `org_id` (optional) — current org so the launcher scopes correctly
 *   - `user_hint` (optional) — email / sub so the launcher can match cached
 *     creds and force a fresh login if the stored token belongs to someone
 *     else (prevents user A's token being reused on user B's session).
 *
 * `base_url` and `stdb_url` use the custom protocol-safe encoding so they
 * survive Windows' protocol handler URL mangling.
 */
export function addBaseUrlToProtocol(args: {
  baseUrl: string;
  stdbUrl: string;
  orgId?: string;
  userHint?: string;
  /** `{stamp}-launcher` DB the launcher must heartbeat into. Without it the
   *  EXE defaults to the house `turbotwin-launcher`, which this portal never
   *  watches — the rocket stays yellow forever. */
  launcherModule?: string;
  /** The stamp's LAUNCHER WorkOS client id. Without it the EXE authenticates
   *  against the house WorkOS project, where minetwin users/orgs don't exist
   *  and the login can never complete. */
  workosClientId?: string;
  /** The stamp webclient host (e.g. the instance URL). The launcher resolves its
   *  config.json there and opens /feedback/crash on it after a crash. */
  domain?: string;
}): string {
  // Hints go through standard query encoding (no colons/slashes inside).
  const hintParams = new URLSearchParams();
  if (args.orgId) hintParams.set('org_id', args.orgId);
  if (args.userHint) hintParams.set('user_hint', args.userHint);
  if (args.launcherModule) hintParams.set('launcher_module', args.launcherModule);
  if (args.workosClientId) hintParams.set('workos_client_id', args.workosClientId);
  if (args.domain) hintParams.set('domain', args.domain);
  const hintQuery = hintParams.toString();

  // Build the final URL with the custom encoding for base_url + stdb_url.
  const parts = [
    `base_url=${encodeUrlForProtocol(args.baseUrl)}`,
    `stdb_url=${encodeUrlForProtocol(args.stdbUrl)}`,
  ];
  if (hintQuery) parts.unshift(hintQuery);
  return `turbotwin://start?${parts.join('&')}`;
}

/**
 * Triggers the OS protocol handoff via a hidden iframe.
 *
 * Why not `window.location.href = protocolUrl`? On Chromium-based browsers
 * that's a top-level navigation: if the OS doesn't have a handler the page
 * navigates to an error state (and our SpacetimeDB WebSockets get torn
 * down with it). The hidden-iframe trick stages the navigation inside an
 * isolated frame — the OS protocol handler still pops the "Open ___?"
 * prompt, but the main page never navigates. Matches the TurboTwin
 * webclient's `openProtocolUrl` at `LaunchService.ts:590`:
 * "Open a protocol URL via hidden iframe (avoids tearing down WebSocket
 * connections)".
 */
export function startHelper(protocolUrl: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = protocolUrl;
  document.body.appendChild(iframe);
  // Give the browser a moment to dispatch the protocol intent before
  // unmounting the iframe (the protocol handler prompt outlives the
  // iframe; this only affects the iframe lifecycle, not the OS dialog).
  setTimeout(() => iframe.remove(), 2000);
}

export type HelperState =
  | { kind: 'not-connected' }
  | { kind: 'connected-offline' }
  | { kind: 'connected-online' }
  // A local launcher API responded, but its signed-in user is a different
  // account than the one signed into this portal tab. Happens when two
  // browsers / two accounts share one machine: launchers are per-identity,
  // and `my_connected_clients` is identity-filtered, so the mismatched tab
  // would otherwise read as "launcher not running" and mislead the user
  // into Start/Download.
  | { kind: 'running-other-user'; email: string | null };

/** Returns true when the launcher EXE is online and reporting heartbeats. */
export function isHelperOnline(state: HelperState): boolean {
  return state.kind === 'connected-online';
}

/** Human-readable status pulled from the four-state icon. */
export function getStatusMessage(state: HelperState): string {
  switch (state.kind) {
    case 'not-connected':
      return 'Not connected to SpacetimeDB';
    case 'connected-offline':
      return 'Launcher not running';
    case 'connected-online':
      return 'Launcher running';
    case 'running-other-user':
      return `Launcher signed in as ${state.email ?? 'a different user'}`;
  }
}

/**
 * Header carrying the launcher's per-instance CSRF nonce; matches
 * `NONCE_HEADER` in turbotwin-launcher/src/local_api.rs. The launcher's
 * `GET /status` returns the nonce and every mutating route (POST /launch etc.)
 * rejects with 403 "invalid or missing launcher nonce" unless the header
 * echoes it. A hostile cross-origin page can't read /status (CORS), so it
 * can't obtain the nonce. Mirrors webclient/src/services/launcher/localApi.ts.
 */
const NONCE_HEADER = 'x-tt-launcher-nonce';

/**
 * Local launcher HTTP API discovery. Parallel launcher instances (one per
 * signed-in user profile) each bind the FIRST FREE port in a small range
 * starting at 19871 — so "the" launcher is not always on 19871: Stefan's
 * 2026-07-29 session bound 19872 because another profile's instance held the
 * base port, and the old single-port probe read as "not running" forever.
 * Mirrors the TurboTwin webclient's localApi.ts; the constants match the
 * launcher's `profile::PORT_BASE` / `PORT_COUNT`.
 */
const LAUNCHER_PORT_BASE = 19871;
const LAUNCHER_PORT_COUNT = 5;

// `targetAddressSpace` is not in lib.dom.d.ts yet. Chrome's Local Network
// Access policy gates fetches from public HTTPS pages into loopback; without
// declaring it Chrome denies the request outright WITHOUT ever showing the
// user the permission prompt. Declaring it makes Chrome prompt once per site.
interface LnaRequestInit extends RequestInit {
  targetAddressSpace?: 'loopback' | 'local';
}

/** WorkOS subject (`user_01…`) of the signed-in portal user, so discovery can
 *  pick THAT user's launcher among parallel instances. Keyed on `sub`, never
 *  email — email is optional per-environment JWT-template config (the minetwin
 *  env lacked it until 2026-07-15) and can change under us; `sub` is standard
 *  and is what SpacetimeDB keys identity on. Set by useLauncherProbeDriver. */
let activeUserSub: string | null = null;
export function setActiveLauncherUser(sub: string | null | undefined): void {
  const next = sub ?? null;
  if (next !== activeUserSub) {
    activeUserSub = next;
    cachedBase = null;
    cachedNonce = null;
  }
}

/** Raw `GET /status` body from a launcher instance. */
type RawLauncherStatus = {
  version?: string;
  org_id?: string | null;
  user_email?: string | null;
  user_sub?: string | null;
  nonce?: string | null;
};

/** Base URL of the discovered launcher instance. Null = rescan the range. */
let cachedBase: string | null = null;

/** Cached per-instance nonce from the last successful /status read. Goes
 *  stale when the launcher restarts (new instance = new nonce), which
 *  httpLaunch handles with one refresh-and-retry. */
let cachedNonce: string | null = null;

async function statusAt(base: string): Promise<RawLauncherStatus | null> {
  try {
    const init: LnaRequestInit = {
      method: 'GET',
      targetAddressSpace: 'loopback',
      signal: AbortSignal.timeout(3000),
    };
    const resp = await fetch(`${base}/status`, init);
    if (!resp.ok) return null;
    return (await resp.json().catch(() => ({}))) as RawLauncherStatus;
  } catch {
    return null;
  }
}

/**
 * Find the launcher instance for the signed-in user: cached base first, then
 * walk the port range. Prefers the instance whose `user_sub` matches
 * `activeUserSub`; falls back to the first reachable one so the UI can still
 * say "running as someone else". Only a sub-match (or a scan with no known
 * sub) caches the base — a mismatch keeps rescanning so the user's own
 * launcher is picked up the moment it appears on a later port.
 */
async function discoverLauncher(): Promise<{ base: string; status: RawLauncherStatus } | null> {
  if (cachedBase) {
    const status = await statusAt(cachedBase);
    if (status) {
      if (status.nonce) cachedNonce = status.nonce;
      return { base: cachedBase, status };
    }
    cachedBase = null;
  }
  let firstReachable: { base: string; status: RawLauncherStatus } | null = null;
  for (let i = 0; i < LAUNCHER_PORT_COUNT; i++) {
    const base = `http://127.0.0.1:${LAUNCHER_PORT_BASE + i}`;
    const status = await statusAt(base);
    if (!status) continue;
    if (!firstReachable) firstReachable = { base, status };
    if (activeUserSub && status.user_sub === activeUserSub) {
      cachedBase = base;
      if (status.nonce) cachedNonce = status.nonce;
      return { base, status };
    }
  }
  if (firstReachable) {
    if (!activeUserSub) cachedBase = firstReachable.base;
    if (firstReachable.status.nonce) cachedNonce = firstReachable.status.nonce;
  }
  return firstReachable;
}

/** The launcher's nonce, from the cache or a fresh discovery. Null on an
 *  older launcher that doesn't return one (it also doesn't require it). */
async function getLauncherNonce(forceRefresh = false): Promise<string | null> {
  if (cachedNonce && !forceRefresh) return cachedNonce;
  cachedNonce = null;
  const found = await discoverLauncher();
  return found?.status.nonce ?? null;
}

/** Result of probing the local launcher HTTP API's `GET /status`. */
export type LauncherLocalStatus =
  | { reachable: false }
  | {
      reachable: true;
      version: string | null;
      orgId: string | null;
      /** Email of the user the launcher EXE is signed in as. `null` on
       *  launcher builds that predate the field or for service tokens. */
      userEmail: string | null;
      userSub: string | null;
    };

/**
 * Probe the locally-running launcher's `GET /status`.
 *
 * Used as a tie-breaker when the STDB-derived state says the launcher is
 * offline: a reachable local API is authoritative proof the EXE is running.
 * Combined with `user_email` it distinguishes three yellow-rocket causes:
 * launcher genuinely not running (unreachable), the STDB subscription went
 * stale on a zombie socket (reachable, same user), or the launcher is signed
 * in as a different account (reachable, other user).
 */
export async function probeLauncherStatus(): Promise<LauncherLocalStatus> {
  const found = await discoverLauncher();
  if (!found) return { reachable: false };
  return {
    reachable: true,
    version: found.status.version ?? null,
    orgId: found.status.org_id ?? null,
    userEmail: found.status.user_email ?? null,
    userSub: found.status.user_sub ?? null,
  };
}

/**
 * Merge the STDB-derived launcher state with the latest local-API probe.
 *
 * The STDB signal (`my_connected_clients` has an App row) wins when positive.
 * When it says offline, a successful probe upgrades the state:
 *   - probe user matches this tab's user -> `connected-online` (the STDB
 *     subscription is stale; the probe driver rebuilds it in the background)
 *   - probe user is a different account -> `running-other-user`
 *   - probe has no user info (legacy launcher) -> stay `connected-offline`,
 *     we can't attribute the running EXE to anyone.
 */
export function resolveHelperState(args: {
  connActive: boolean;
  stdbSaysRunning: boolean;
  probe: {
    status: 'idle' | 'unreachable' | 'running';
    email: string | null;
    sub?: string | null;
  };
  myEmail: string | null;
  /** WorkOS subject of this tab's user — the preferred match key. Email is
   *  optional per-environment JWT-template config; `sub` is always present. */
  mySub?: string | null;
}): HelperState {
  if (!args.connActive) return { kind: 'not-connected' };
  if (args.stdbSaysRunning) return { kind: 'connected-online' };
  if (args.probe.status === 'running') {
    if (args.mySub && args.probe.sub && args.probe.sub === args.mySub) {
      return { kind: 'connected-online' };
    }
    if (
      args.myEmail &&
      args.probe.email &&
      args.probe.email.toLowerCase() === args.myEmail.toLowerCase()
    ) {
      return { kind: 'connected-online' };
    }
    // Reachable but attributable to someone else. A legacy launcher with no
    // user info at all stays `connected-offline` — we can't attribute it.
    if (args.probe.sub || args.probe.email) {
      return { kind: 'running-other-user', email: args.probe.email };
    }
  }
  return { kind: 'connected-offline' };
}

/** Minimal shape of an `app_version` row needed for launch resolution. */
interface AppVersionLike {
  appId: string;
  versionId: bigint;
  version: string;
  channel: { tag: string };
  publishedAt?: { toDate(): Date } | undefined;
}

/** Minimal shape of a `channel_version` (pinned-version pointer) row. */
interface ChannelVersionLike {
  appId: string;
  channel: { tag: string };
  currentVersionId: bigint;
}

/**
 * Resolve which app_version to launch for a channel.
 *
 * The launcher's channel model has two INDEPENDENT concepts:
 *  - `app_version.channel` is the channel a build was *published* under
 *    (immutable, set at upload).
 *  - `channel_version.current_version_id` is the *pinned* version a channel
 *    currently points at. It can reference a build published under ANY channel
 *    (e.g. the Stable channel pinned to a Dev-published build — exactly the
 *    "Pinned version: v21 (Dev)" case in the Edit App UI).
 *
 * So we follow the pin FIRST. The old behavior (filter `app_version` by
 * published channel and take the latest) returns nothing for a channel that has
 * no natively-published builds — which is why selecting Stable showed "No
 * app_version rows ... in Stable channel" even though Stable was pinned to v21.
 *
 * Fallback order: pinned version -> latest published on this channel (legacy) ->
 * undefined (caller omits version_id and the launcher uses its installed
 * default).
 */
export function resolveLaunchVersion(
  appVersions: readonly AppVersionLike[],
  channelVersions: readonly ChannelVersionLike[],
  appId: string,
  channel: string,
): { versionId: bigint; version: string } | undefined {
  const pin = channelVersions.find(
    (cv) => cv.appId === appId && cv.channel.tag === channel,
  );
  if (pin) {
    const pinned = appVersions.find((av) => av.versionId === pin.currentVersionId);
    if (pinned) return { versionId: pinned.versionId, version: pinned.version };
  }
  const latest = appVersions
    .filter((av) => av.appId === appId && av.channel.tag === channel)
    .sort(
      (a, b) =>
        (b.publishedAt?.toDate().getTime() ?? 0) -
        (a.publishedAt?.toDate().getTime() ?? 0),
    )[0];
  if (latest) return { versionId: latest.versionId, version: latest.version };
  return undefined;
}

/**
 * Result of a successful `/launch` call.
 *  - `launched`: the requested version was already installed and the app was
 *    started immediately (HTTP 200).
 *  - `downloading`: the version wasn't installed; the launcher is downloading it
 *    in the background and will launch automatically when it finishes (HTTP 202).
 *    Watch `my_download_progress` (filtered to the version) for progress + result.
 */
export type LaunchStatus = 'launched' | 'downloading';

/**
 * Ask the locally-running launcher EXE to launch an app via its HTTP API.
 * On success returns the launcher's status (`launched` vs `downloading`) so the
 * caller can show download progress; on any failure returns `{ ok:false }` so the
 * caller can fall back to the STDB `queueLaunch` reducer or surface the error.
 */
export async function httpLaunch(args: {
  appId: string;
  exePath: string;
  versionId?: bigint;
  launchArgs?: string;
}): Promise<
  | { ok: true; status: LaunchStatus }
  // `unreachable` is true only when the local launcher API couldn't be contacted
  // at all (connection refused / timeout) — the one case where queuing via STDB
  // is a sensible fallback. A non-2xx response is a real launcher error and
  // should be surfaced, not silently re-queued.
  | { ok: false; reason: string; unreachable?: boolean }
> {
  const postLaunch = async (base: string, nonce: string | null): Promise<Response> => {
    const init: LnaRequestInit = {
      method: 'POST',
      targetAddressSpace: 'loopback',
      headers: {
        'Content-Type': 'application/json',
        // Per-instance CSRF nonce; mutating launcher routes 403 without it.
        ...(nonce ? { [NONCE_HEADER]: nonce } : {}),
      },
      body: JSON.stringify({
        app_id: args.appId,
        exe_path: args.exePath,
        launch_args: args.launchArgs ?? '',
        // Serialize bigint as a JSON number; u64s for realistic version
        // counts fit Number safely.
        ...(args.versionId !== undefined
          ? { version_id: Number(args.versionId) }
          : {}),
      }),
      signal: AbortSignal.timeout(5000),
    };
    return fetch(`${base}/launch`, init);
  };
  try {
    // Route to THIS user's launcher instance (discovery caches the base, and
    // the /status read it does also refreshes the nonce — no extra trip).
    const found = await discoverLauncher();
    if (!found) {
      return { ok: false, reason: 'Local launcher API unreachable', unreachable: true };
    }
    let resp = await postLaunch(found.base, await getLauncherNonce());
    if (resp.status === 403) {
      // Cached nonce can be stale after a launcher restart (each instance
      // mints a fresh one). Re-read /status once and retry.
      resp = await postLaunch(found.base, await getLauncherNonce(true));
    }
    if (resp.ok) {
      // 200 "launched" vs 202 "downloading" — the body carries the explicit
      // status; fall back to inferring from the HTTP code if it's missing.
      const body = (await resp.json().catch(() => ({}))) as { status?: string };
      const status: LaunchStatus =
        body.status === 'downloading' || resp.status === 202
          ? 'downloading'
          : 'launched';
      return { ok: true, status };
    }
    const body = (await resp.json().catch(() => ({}))) as { error?: string };
    return { ok: false, reason: body.error ?? `HTTP ${resp.status}` };
  } catch (err) {
    // Only a TypeError means nobody received the POST (connection refused,
    // CORS, LNA). A TimeoutError means the launcher HAS the request and is
    // still in its synchronous spawn preflight; `launch_app` has no await
    // point to cancel at, so it will finish the spawn anyway. Reporting that
    // as unreachable made the caller queue a second launch via STDB and the
    // user got two copies of the app (customer report, 2026-09-16).
    const unreachable = err instanceof TypeError;
    const reason = unreachable
      ? 'Local launcher API unreachable'
      : err instanceof DOMException && err.name === 'TimeoutError'
        ? 'The launcher accepted the request but is slow to respond; the app should appear shortly.'
        : err instanceof Error
          ? err.message
          : 'Local launcher API unreachable';
    return { ok: false, reason, unreachable };
  }
}


/** Minimal `my_org_apps` row shape for app resolution. */
interface OrgAppLike {
  appId: string;
  displayName?: string | null;
  orgId?: string;
}

/**
 * Resolve which launcher app the portal launches, org-aware
 * (MULTI-ORG-PORTAL plan):
 *   1. exact match on the configured id (`VITE_MINEZERO_APP_ID`),
 *   2. legacy fuzzy match on "minezero" in appId/displayName,
 *   3. the org's own app when it is unambiguous — `my_org_apps` returns
 *      platform apps (orgId "") plus the session org's apps, so for a
 *      customer org whose app isn't named "minezero" (e.g. South32's
 *      `umar_sandbox`) the sole org-homed row is the right one.
 * Ambiguity (several org apps, none matching) resolves to undefined and the
 * launch UI shows its existing "no app" message naming the env override.
 */
/**
 * Whether the version the channel resolves to is already on THIS machine
 * (#193). Before this, "is it installed?" was only discovered after the
 * click, when the local launcher answered `202 downloading` — so a user
 * could not tell a one-second launch from a multi-GB download.
 *
 * `unknown` means we cannot say (no resolved version): the caller keeps the
 * plain Launch label, since the launcher will use its installed default.
 */
export type InstallState = 'installed' | 'update' | 'download' | 'unknown';

export function resolveInstallState(params: {
  installed: readonly { appId: string; versionId: bigint }[];
  appId: string;
  versionId: bigint | undefined;
}): InstallState {
  const { installed, appId, versionId } = params;
  if (versionId === undefined) return 'unknown';
  const forApp = installed.filter((r) => r.appId === appId);
  if (forApp.some((r) => r.versionId === versionId)) return 'installed';
  return forApp.length > 0 ? 'update' : 'download';
}

/** A download the user would want to see, whatever version it is for. */
export type AppDownload = {
  versionId: bigint;
  status: 'NotStarted' | 'InProgress' | 'Paused' | 'Completed' | 'Failed';
  /** 0-100, or null while the total size is unknown. */
  percent: number | null;
  statusMessage: string | null;
  errorMessage: string | null;
};

/** Downloads in these states are still going — the indicator stays up. */
export function isDownloadActive(status: string | undefined): boolean {
  return status === 'InProgress' || status === 'Paused';
}

/**
 * The newest download-progress row belonging to THIS app (#194), whatever
 * version — a global indicator must survive a channel switch, and the
 * progress rows carry no app_id, so the caller passes the app's version
 * ids. Latest `updatedAt` wins, so a fresh attempt supersedes a stale
 * Completed/Failed row.
 */
export function latestAppDownload<
  T extends {
    versionId: bigint;
    status: { tag?: string };
    bytesDownloaded: bigint;
    totalBytes: bigint;
    updatedAt: { toDate: () => Date };
    statusMessage?: string | null;
    errorMessage?: string | null;
  },
>(rows: readonly T[], appVersionIds: ReadonlySet<string>): AppDownload | null {
  const mine = rows.filter((r) => appVersionIds.has(String(r.versionId)));
  if (mine.length === 0) return null;
  const row = mine.reduce((latest, r) =>
    r.updatedAt.toDate() > latest.updatedAt.toDate() ? r : latest,
  );
  const percent =
    row.totalBytes > 0n
      ? Math.min(100, Math.round(Number((row.bytesDownloaded * 100n) / row.totalBytes)))
      : null;
  return {
    versionId: row.versionId,
    status: (row.status.tag ?? 'NotStarted') as AppDownload['status'],
    percent,
    statusMessage: row.statusMessage ?? null,
    errorMessage: row.errorMessage ?? null,
  };
}

export function resolveOrgApp<T extends OrgAppLike>(
  apps: readonly T[],
  preferredAppId: string,
  /** The admin's remembered per-org pick (launchAppChoice) — wins when it
   *  still names a visible app; silently ignored otherwise (the app may
   *  have been deleted or the org switched). */
  rememberedAppId?: string | null,
): T | undefined {
  if (rememberedAppId) {
    const remembered = apps.find((a) => a.appId === rememberedAppId);
    if (remembered) return remembered;
  }
  const exact = apps.find((a) => a.appId === preferredAppId);
  if (exact) return exact;
  const fuzzy = apps.find(
    (a) => /minezero/i.test(a.appId) || /minezero/i.test(a.displayName ?? ''),
  );
  if (fuzzy) return fuzzy;
  const orgApps = apps.filter((a) => (a.orgId ?? '') !== '');
  return orgApps.length === 1 ? orgApps[0] : undefined;
}
